/**
 * Pike's spray bay: what a car can be given in garage mode (paint, finish, rims and rim colour, a neon
 * underglow, ride height), what each costs, and the compact form the mods travel in online.
 *
 * Shared by the offline game (src/garage: the bay, the menu, the wallet, the save) and the online server
 * (server/economy/garage.ts validates and prices every purchase with THESE functions; the client never
 * sends a price). Visual only: nothing here changes how a car drives, online or off.
 *
 * Paint is not a "mod": it is the respray (src/economy resprayCost / shared/economy resprayCost), priced by
 * the wanted level, and it keeps the respray's rules — new plates, repaired, heat off.
 */

export type Finish = 'gloss' | 'metallic' | 'matt';
export type RideHeight = -1 | 0 | 1;

/** Everything Pike can bolt on besides the paint. `null` / 0 = as the car came. */
export interface CarMods {
  /** Rim style id (RIMS; a `WheelStyle` in src/vehicles/meshes.ts), or null = the model's own wheels. */
  wheels: string | null;
  /** Rim colour (RIM_COLOURS hex), or null = the wheel's own finish. */
  rim: number | null;
  /** Underglow colour (NEON_COLOURS hex), or null = none. */
  neon: number | null;
  /** -1 lowered, 0 stock, +1 raised (visual ride height only). */
  ride: RideHeight;
  /** Paint finish, or null = as it came (the car's own solid or metallic coat). */
  finish: Finish | null;
  /** Negative camber step (CAMBERS index): 0 = 0°, 1 = -3°, 2 = -6°, 3 = -10° (visual wheel tilt). */
  camber: number;
  /** Track width step (TRACKS id): -1 tucked, 0 stock, 1 wide, 2 extra wide (visual wheel offset). */
  track: number;
  /**
   * The gang livery (WP-X4, shared/hideout.ts): the colour on the car's trim (stripes, roof, band), or null. Only
   * the server puts it on (`hideoutLivery`, from the gang's colour); ordinary spray-bay work never changes it.
   */
  livery: number | null;
}

export interface Swatch {
  id: string;
  name: string;
  hex: number;
}

/** Period colours first, then a few louder ones. */
export const GARAGE_PAINTS: readonly Swatch[] = [
  { id: 'pillarbox', name: 'Pillar-box red', hex: 0xa3201b },
  { id: 'brooklands', name: 'Brooklands green', hex: 0x1f4a2e },
  { id: 'harvest', name: 'Harvest gold', hex: 0xc8a23a },
  { id: 'midnight', name: 'Midnight blue', hex: 0x1a2440 },
  { id: 'tangerine', name: 'Tangerine', hex: 0xd2642a },
  { id: 'bronze', name: 'Conker bronze', hex: 0x7a5230 },
  { id: 'aqua', name: 'Aqua', hex: 0x3d8a8a },
  { id: 'aubergine', name: 'Aubergine', hex: 0x4a2440 },
  { id: 'ermine', name: 'Ermine white', hex: 0xe8e4d6 },
  { id: 'jet', name: 'Jet black', hex: 0x141414 },
  { id: 'lime', name: 'Lime flash', hex: 0x86b82a },
  { id: 'violet', name: 'Purple haze', hex: 0x5e2a86 },
  { id: 'signal', name: 'Signal yellow', hex: 0xe8b80c },
  { id: 'electric', name: 'Electric blue', hex: 0x1f62c4 },
];

export interface RimStyle {
  /** A `WheelStyle` id (src/vehicles/meshes.ts). */
  id: string;
  name: string;
  price: number;
}

/**
 * Pike's card, cheapest first. The first five are the period wheels (procedural, meshes.ts); the premium six
 * after them are Blender-modelled (src/vehicles/rimModels.ts, art/models/blender/rims_gen.py) with their own
 * default finish — a rim colour (RIM_COLOURS) paints the face and the well, never a polished lip, nuts or cap.
 * Original designs with made-up names: no maker's names or marks anywhere on them or here.
 */
export const RIMS: readonly RimStyle[] = [
  { id: 'steel', name: 'Painted steels', price: 60 },
  { id: 'rostyle', name: 'Rostyle-pattern', price: 140 },
  { id: 'chrome', name: 'Ribbed chrome', price: 160 },
  { id: 'minilite', name: 'Eight-spoke alloys', price: 180 },
  { id: 'wire', name: 'Wire wheels', price: 240 },
  { id: 'ninestar', name: 'Nine-star', price: 280 },
  { id: 'twelve', name: 'Featherlight twelve', price: 300 },
  { id: 'forged', name: 'Six-spoke forged', price: 320 },
  { id: 'twinspoke', name: 'Twin-spoke racer', price: 340 },
  { id: 'honeycomb', name: 'Honeycomb', price: 380 },
  { id: 'lattice', name: 'Lattice two-piece', price: 420 },
];

export const RIM_COLOURS: readonly Swatch[] = [
  { id: 'silver', name: 'Silver', hex: 0xb8bab6 },
  { id: 'gunmetal', name: 'Gunmetal', hex: 0x3a3c3e },
  { id: 'black', name: 'Gloss black', hex: 0x121212 },
  { id: 'white', name: 'White', hex: 0xe8e6dc },
  { id: 'gold', name: 'Gold', hex: 0xc9a227 },
  { id: 'red', name: 'Red', hex: 0xa3201b },
  { id: 'bronze', name: 'Bronze', hex: 0x8a5a2a },
];

export const NEON_COLOURS: readonly Swatch[] = [
  { id: 'red', name: 'Red', hex: 0xff2a2a },
  { id: 'amber', name: 'Amber', hex: 0xffa020 },
  { id: 'green', name: 'Green', hex: 0x30ff60 },
  { id: 'blue', name: 'Blue', hex: 0x2a6aff },
  { id: 'violet', name: 'Violet', hex: 0xb040ff },
  { id: 'pink', name: 'Pink', hex: 0xff40b0 },
  { id: 'white', name: 'White', hex: 0xe8f0ff },
];

export const FINISHES: readonly { id: Finish; name: string; price: number }[] = [
  { id: 'gloss', name: 'Solid gloss', price: 40 },
  { id: 'metallic', name: 'Metallic flake', price: 90 },
  { id: 'matt', name: 'Flat matt', price: 70 },
];

/** Camber steps: the wheels lean in at the top (degrees, shown as negative camber). */
export const CAMBERS: readonly { id: number; name: string; deg: number }[] = [
  { id: 0, name: 'Straight (0°)', deg: 0 },
  { id: 1, name: 'A touch (−3°)', deg: 3 },
  { id: 2, name: 'Slammed (−6°)', deg: 6 },
  { id: 3, name: 'Silly (−10°)', deg: 10 },
];

/** Track steps: how far the wheels stand out of the arches (metres per side). */
export const TRACKS: readonly { id: number; name: string; offset: number }[] = [
  { id: -1, name: 'Tucked in', offset: -0.05 },
  { id: 0, name: 'Stock', offset: 0 },
  { id: 1, name: 'Wide', offset: 0.06 },
  { id: 2, name: 'Extra wide', offset: 0.12 },
];

export const RIDES: readonly { id: RideHeight; name: string }[] = [
  { id: -1, name: 'Lowered' },
  { id: 0, name: 'Stock' },
  { id: 1, name: 'Raised' },
];

/** Prices (whole pounds). Modest on purpose: money sinks, not a second economy. */
export const MOD_PRICES = {
  /** Rim colour (any swatch, or back to the wheel's own finish). */
  rimColour: 50,
  /** Fit an underglow or change its colour. */
  neon: 150,
  /** Take the underglow off. */
  neonOff: 20,
  /** Lower or raise. */
  ride: 120,
  /** Back to stock springs. */
  rideStock: 40,
  /** Back to the model's own wheels. */
  wheelsStock: 30,
  /** Back to the car's own coat. */
  finishStock: 30,
  /** Any camber setting (back to straight: `stanceStock`). */
  camber: 80,
  /** Any track width (back to stock: `stanceStock`). */
  track: 100,
  stanceStock: 30,
} as const;

/** Pike makes a car yours (offline: the save's one car, kept in Lockup 7; online: an owned car on the server). */
export const KEEP_PRICE = 300;

export function stockMods(): CarMods {
  return { wheels: null, rim: null, neon: null, ride: 0, finish: null, camber: 0, track: 0, livery: null };
}

export function sameMods(a: CarMods, b: CarMods): boolean {
  return a.wheels === b.wheels && a.rim === b.rim && a.neon === b.neon && a.ride === b.ride && a.finish === b.finish && a.camber === b.camber && a.track === b.track && a.livery === b.livery;
}

export function isStock(m: CarMods): boolean {
  return sameMods(m, stockMods());
}

export type ModKey = 'wheels' | 'rim' | 'neon' | 'ride' | 'finish' | 'camber' | 'track';
/** A livery colour as it may travel (any 24-bit colour: the gang chose it from its own palette). */
const isLivery = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 0xffffff;

export interface PriceLine {
  key: ModKey;
  price: number;
}

/** What each changed item costs going from `cur` to `want` (unchanged items cost nothing; the livery is the hideout's, never priced here). */
export function modLines(cur: CarMods, want: CarMods): PriceLine[] {
  const out: PriceLine[] = [];
  if (want.finish !== cur.finish) out.push({ key: 'finish', price: want.finish === null ? MOD_PRICES.finishStock : FINISHES.find((f) => f.id === want.finish)?.price ?? 0 });
  if (want.wheels !== cur.wheels) out.push({ key: 'wheels', price: want.wheels === null ? MOD_PRICES.wheelsStock : RIMS.find((r) => r.id === want.wheels)?.price ?? 0 });
  if (want.rim !== cur.rim) out.push({ key: 'rim', price: MOD_PRICES.rimColour });
  if (want.neon !== cur.neon) out.push({ key: 'neon', price: want.neon === null ? MOD_PRICES.neonOff : MOD_PRICES.neon });
  if (want.ride !== cur.ride) out.push({ key: 'ride', price: want.ride === 0 ? MOD_PRICES.rideStock : MOD_PRICES.ride });
  if (want.camber !== cur.camber) out.push({ key: 'camber', price: want.camber === 0 ? MOD_PRICES.stanceStock : MOD_PRICES.camber });
  if (want.track !== cur.track) out.push({ key: 'track', price: want.track === 0 ? MOD_PRICES.stanceStock : MOD_PRICES.track });
  return out;
}

export function modsPrice(cur: CarMods, want: CarMods): number {
  let t = 0;
  for (const l of modLines(cur, want)) t += l.price;
  return t;
}

const inPalette = (pal: readonly Swatch[], v: unknown): v is number => typeof v === 'number' && pal.some((s) => s.hex === v);

/**
 * Strict check (the server, before it charges): every field must be a known option. Returns the mods or
 * null when anything is off (an unknown rim, a colour that isn't on the card, a ride height of 7).
 */
export function parseModsStrict(raw: { wheels?: unknown; rim?: unknown; neon?: unknown; ride?: unknown; finish?: unknown; camber?: unknown; track?: unknown; livery?: unknown }): CarMods | null {
  const wheels = raw.wheels === undefined || raw.wheels === null ? null : RIMS.some((r) => r.id === raw.wheels) ? (raw.wheels as string) : undefined;
  const rim = raw.rim === undefined || raw.rim === null ? null : inPalette(RIM_COLOURS, raw.rim) ? raw.rim : undefined;
  const neon = raw.neon === undefined || raw.neon === null ? null : inPalette(NEON_COLOURS, raw.neon) ? raw.neon : undefined;
  const ride = raw.ride === undefined || raw.ride === null ? 0 : raw.ride === -1 || raw.ride === 0 || raw.ride === 1 ? raw.ride : undefined;
  const finish = raw.finish === undefined || raw.finish === null ? null : FINISHES.some((f) => f.id === raw.finish) ? (raw.finish as Finish) : undefined;
  const camber = raw.camber === undefined || raw.camber === null ? 0 : CAMBERS.some((c) => c.id === raw.camber) ? (raw.camber as number) : undefined;
  const track = raw.track === undefined || raw.track === null ? 0 : TRACKS.some((t) => t.id === raw.track) ? (raw.track as number) : undefined;
  const livery = raw.livery === undefined || raw.livery === null ? null : isLivery(raw.livery) ? raw.livery : undefined;
  if (wheels === undefined || rim === undefined || neon === undefined || ride === undefined || finish === undefined || camber === undefined || track === undefined || livery === undefined) return null;
  return { wheels, rim, neon, ride, finish, camber, track, livery };
}

/** Lenient clean-up (a save, a snapshot): unknown fields fall back to stock, never throws. */
export function sanitizeMods(raw: unknown): CarMods {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return stockMods();
  const r = raw as Record<string, unknown>;
  const one = <K extends keyof CarMods>(k: K): CarMods[K] => {
    const m = parseModsStrict({ [k]: r[k] });
    return m ? m[k] : stockMods()[k];
  };
  return { wheels: one('wheels'), rim: one('rim'), neon: one('neon'), ride: one('ride'), finish: one('finish'), camber: one('camber'), track: one('track'), livery: one('livery') };
}

// ---------------------------------------------------------------------------------------- wire

/** Mods on the wire (snapshot metadata, protocol v15): only the non-stock fields, short keys. */
export interface WireMods {
  /** rim style id */
  w?: string;
  /** rim colour */
  r?: number;
  /** neon colour */
  g?: number;
  /** ride height -1 | 1 */
  h?: number;
  /** finish: 1 gloss, 2 metallic, 3 matt */
  f?: number;
  /** camber step 1..3 */
  c?: number;
  /** track step -1 | 1 | 2 */
  t?: number;
  /** the gang livery's colour (protocol v19) */
  l?: number;
}

const FINISH_CODE: Record<Finish, number> = { gloss: 1, metallic: 2, matt: 3 };

export function encodeMods(m: CarMods): WireMods | undefined {
  if (isStock(m)) return undefined;
  const w: WireMods = {};
  if (m.wheels !== null) w.w = m.wheels;
  if (m.rim !== null) w.r = m.rim;
  if (m.neon !== null) w.g = m.neon;
  if (m.ride !== 0) w.h = m.ride;
  if (m.finish !== null) w.f = FINISH_CODE[m.finish];
  if (m.camber !== 0) w.c = m.camber;
  if (m.track !== 0) w.t = m.track;
  if (m.livery !== null) w.l = m.livery;
  return w;
}

export function decodeMods(w: WireMods | undefined | null): CarMods {
  if (!w || typeof w !== 'object') return stockMods();
  const finish = (Object.keys(FINISH_CODE) as Finish[]).find((k) => FINISH_CODE[k] === w.f) ?? null;
  return sanitizeMods({ wheels: w.w, rim: w.r, neon: w.g, ride: w.h, finish, camber: w.c, track: w.t, livery: w.l });
}

export function swatchName(pal: readonly Swatch[], hex: number | null, none = 'None'): string {
  if (hex === null) return none;
  return pal.find((s) => s.hex === hex)?.name ?? 'Custom';
}

// ---------------------------------------------------------------------------------------- stance

/**
 * How the wheels actually sit for a set of mods (the car view reads this; visual only). Combinations that
 * would put a tyre through the body are tucked out of the way rather than refused:
 *  - lowered + tucked: a lowered body sits on top of tucked tyres, so the wheels stay at stock track;
 *  - heavy camber pulls the top of the tyre in toward the arch: the wheel is pushed out by half the lean
 *    (and a little more when lowered), so the tyre top stays where a straight tyre's would.
 * `offset` is metres outward per side, `camber` radians (top in).
 */
export function wheelStance(m: { ride: number; camber: number; track: number }, radius = 0.32): { camber: number; offset: number } {
  const deg = CAMBERS.find((c) => c.id === m.camber)?.deg ?? 0;
  const camber = (deg * Math.PI) / 180;
  let offset = TRACKS.find((t) => t.id === m.track)?.offset ?? 0;
  if (m.ride < 0 && offset < 0) offset = 0;
  offset += radius * Math.sin(camber) * (m.ride < 0 ? 0.75 : 0.5);
  return { camber, offset };
}
